// ChartRenderer.tsx
import React from "react";
import {
    Chart as ChartJS,
    ChartData,
    ChartOptions,
    ChartType,
    ArcElement,
    TimeScale,
    LineController,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    BarElement
} from "chart.js";
import "chartjs-adapter-date-fns"; // Import the date adapter
import { Line, Bar, Pie, Doughnut, Radar, PolarArea, Bubble, Scatter } from "react-chartjs-2";
import JSON5 from "json5";

interface ChartConfig {
    type: ChartType;
    data: ChartData;
    options?: ChartOptions;
}

interface ChartRendererProps {
    configJson: string;
}
ChartJS.register(ArcElement, TimeScale, LineController, LineElement, PointElement, LinearScale, CategoryScale, BarElement);
const ChartRenderer: React.FC<ChartRendererProps> = ({ configJson }) => {
    let config: ChartConfig;
    let html: JSX.Element = <div></div>;
    try {
        let configJ = JSON.parse(configJson);
        if (typeof configJ === "string") {
            config = JSON.parse(configJ) as ChartConfig;
        } else {
            if (configJ.chartjs_json) {
                config = configJ.chartjs_json as ChartConfig;
            } else {
                config = configJ as ChartConfig;
            }
        }

        let { type, data, options } = config;

        // Chart type mapping
        let chartComponents: { [key in ChartType]: React.FC<any> } = {
            line: Line,
            bar: Bar,
            pie: Pie,
            doughnut: Doughnut,
            radar: Radar,
            polarArea: PolarArea,
            bubble: Bubble,
            scatter: Scatter
        };
        const ChartComponent = chartComponents[type];

        if (!ChartComponent) return <div></div>;

        html = (
            <div
                style={{
                    background: "#fff",
                    borderRadius: 16,
                    boxShadow: "0 2px 12px 0 rgba(0,0,0,0.08)",
                    padding: 24,
                    margin: "24px auto",
                    maxWidth: 700,
                    minWidth: 320,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontFamily: "Urbanist, Roboto, sans-serif",
                    fontSize: 8
                }}
            >
                <ChartComponent
                    data={data}
                    options={{
                        ...options,
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: {
                                display: true,
                                position: "top",
                                labels: {
                                    font: { size: 11 },
                                    color: "#333",
                                    padding: 5
                                }
                            },
                            title: {
                                font: { size: 18 }
                            },
                            ...((options && (options as any).plugins) || {})
                        },
                        layout: {
                            padding: 5,
                            ...((options && (options as any).layout) || {})
                        },
                        interaction: {
                            mode: "nearest",
                            intersect: false,
                            ...((options && (options as any).interaction) || {})
                        },
                        font: {
                            family: "Urbanist, Roboto, sans-serif",
                            size: 10,
                            weight: "normal",
                            lineHeight: 1.2,
                            style: "normal"
                        }
                    }}
                    height={350}
                />
            </div>
        );
    } catch (error: any) {
        console.error("error rendering chart:", error);
        return <div></div>;
    }

    return html;
};

export default ChartRenderer;
